"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.initialize = initialize;
var _coreMessaging = _interopRequireDefault(require("../../core-messaging"));
var _coreSanitize = _interopRequireDefault(require("../../core-sanitize"));
var _coreUuid = _interopRequireDefault(require("../../core-uuid"));
var _destroy2 = require("./destroy");
var _display2 = require("./display");
var _hide2 = require("./hide");
var _show2 = require("./show");
var _callback = require("../dispatch/callback");
var _changeTitle = require("../dispatch/change-title");
var _getConfig = require("../get-config");
var _openWindow = require("../dispatch/open-window");
var _resizeIframe = require("../dispatch/resize-iframe");
var _scrollTop = require("../dispatch/scroll-top");
var _sendConfig = require("../dispatch/send-config");
var _triggerBroadcast = require("../dispatch/trigger-broadcast");
var _error = require("../error");
var _context = require("../context");
var _validateConfig = require("../validate-config");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
/*
* Copyright (c) 2020 Wells Fargo, All rights reserved.
*
* This software is the confidential and proprietary information of Wells Fargo
* bank. ('Confidential Information'). You shall not disclose such Confidential
* Information and shall use it only in accordance with the terms of the license
* agreement you entered into with WellsFargo.
*/

/**
 * Initialize IFrame
 * - initializes an SDK iframe
 *
 * @module core-bootstrap-command-initialize
 */

/**
* Empties a DOM node
*
* @param {*} node The node to empty.
*/
function emptyNode(node) {
  while (node.hasChildNodes()) {
    node.removeChild(node.firstChild);
  }
}

/**
 * Send the initialize command to the SDK loaded in an iframe
 *
 * @param {object} iframeEntry iframe entry object with configuration data
 */
function sendInitialize(iframeEntry) {
  _coreMessaging["default"].sendInitialize(iframeEntry.element.contentWindow, iframeEntry.nonce, iframeEntry.id);
}

/**
 * Sets up an iframe to load and present an SDK instance
 *
 * @param {*} iframeEntry Configuration data for the iframe SDK
 * @param {string} srcUrl The iframe src URL to initialize the iframe
 * @param {string} title The initial iframe title
 */
function setupIframe(iframeEntry, srcUrl, title) {
  var targetDiv = document.getElementById(iframeEntry.config.targetDivId);
  emptyNode(targetDiv);
  var iframe = document.createElement('iframe');

  // allow-modals is required for an iframe to print itself
  iframe.sandbox = 'allow-same-origin allow-scripts allow-forms allow-modals allow-popups ' + 'allow-top-navigation allow-downloads';
  iframe.src = _coreSanitize["default"].sanitizeAll(srcUrl);
  iframe.allow = 'camera self';
  if (iframeEntry.config.silentInit) {
    (0, _hide2.hideElement)(iframe);
  } else {
    (0, _show2.showElement)(iframe);
  }
  iframe.setAttribute('scrolling', 'no');
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allowfullscreen', 'true');
  iframe.setAttribute('tabindex', '0');
  iframe.name = iframeEntry.id;
  iframe.id = iframeEntry.id;
  iframe.width = '100%';
  iframe.height = 450;
  iframe.title = _coreSanitize["default"].sanitizeAll(title);

  // add the iframe to the DOM
  targetDiv.appendChild(iframe);

  // keep a handle to the content window for posting events
  iframeEntry.element = iframe;
  iframeEntry.initialized = false;

  // register the iframe load listener
  iframe.addEventListener('load', function () {
    if (!iframeEntry.initialized) {
      // initialize the SDK
      sendInitialize(iframeEntry);
      // prevent re-initialization
      iframeEntry.initialized = true;
    } else {
      // log error
      (0, _error.handleError)('SDK IFRAME load event was triggered after first load.');
    }
  });
}

/**
* Initialize one SDK in an IFRAME
*  - initializes the iframe entry with ID, handle, element, config for later lookup
*  - validates the SDK configuration
*  - initializes iframe messaging dispatch
*    - **resizeIframe** Resizes the IFRAME DOM element
*    - **scrollTop** Scrolls the scrolling container for this IFRAME DOM element to the top
*    - **changeTitle** Changes the title of this IFRAME DOM element
*    - **getConfig** Sends the SDK configuration object back to the IFRAME that requested it
*    - **openWindow** Opens a new browser window/tab in parent DOM
*    - **triggerBroadcast** Sends a **broadcast** action back to originating SDK with IDs
* and nonces for all other IFRAMES
*    - **callback** Invokes a callback function
*
* @param {object} [config] An optional configuration object
* @param {string} iframePath The src url for this iframe
* @param {string} iframeInitialTitle The initial title for this iframe
* @return {function} An SDK proxy function that allows API access to the SDK instance
* created by this initialize call.
*/
function initialize(config, iframePath, iframeInitialTitle) {
  var newIframeId = _coreUuid["default"].uuid();
  if (!newIframeId) {
    // this browser is not supported and cannot generate UUIDs
    (0, _error.handleError)('Unsupported browser does not have have a cryptographically secure PRNG');
    return null;
  }
  var newIframeEntry = {
    id: newIframeId,
    handle: undefined,
    element: undefined,
    nonce: undefined,
    config: undefined
  };
  (0, _context.getContext)().iframes.push(newIframeEntry);

  // get the SDK configuration for this iframe
  var sdkConfig = (0, _getConfig.getConfig)(config);
  if (!(0, _validateConfig.validateConfig)(sdkConfig)) {
    return null;
  } else {
    sdkConfig.startTime = Date.now();
    newIframeEntry.config = sdkConfig;
    (0, _context.setTargetOrigin)(sdkConfig.sdkUrl);
  }
  _coreMessaging["default"].initialize({
    'resizeIframe': _resizeIframe.resizeIframe,
    'scrollTop': _scrollTop.scrollTop,
    'changeTitle': _changeTitle.changeTitle,
    'getConfig': _sendConfig.sendConfig,
    'openWindow': _openWindow.openWindow,
    'triggerBroadcast': _triggerBroadcast.triggerBroadcast,
    'callback': _callback.callback
  }, (0, _context.getContext)().iframeTargetOrigin);

  // Set WFRequest ID and pass to srcUrl as urlParam
  var WFRequestID = _coreUuid["default"].uuid();
  // Set clientOrigin and pass to srcUrl as urlParam
  var clientOrigin = base64URLEncode(window.origin);
  // Set gatewayEntityId and pass to srcUrl as urlParam
  var gatewayEntityId = base64URLEncode(newIframeEntry.config.gatewayEntityId);
  // Set additionalAncestors and pass to srcUrl as urlParam
  var additionalAncestors = base64URLEncode(newIframeEntry.config.additionalAncestors);
  var iframeSrcUrl = _coreSanitize["default"].sanitizeAll(newIframeEntry.config.sdkUrl + iframePath + "?WFRequestID=".concat(WFRequestID, "&o=").concat(clientOrigin) + "&id=".concat(gatewayEntityId, "&aa=").concat(additionalAncestors));
  setupIframe(newIframeEntry, iframeSrcUrl, iframeInitialTitle);

  // return a SDK proxy function that stores the iframeID and allows
  // easy API access to a specific iframe/SDK
  /* eslint-disable */
  return {
    id: newIframeId,
    display: function display(screenName, opts) {
      return (0, _display2.display)(screenName, opts, newIframeId);
    },
    show: function show() {
      return (0, _show2.show)(newIframeId);
    },
    hide: function hide() {
      return (0, _hide2.hide)(newIframeId);
    },
    destroy: function destroy() {
      return (0, _destroy2.destroy)(newIframeId);
    }
  };
  /* eslint-enable */
}
var base64URLEncode = function base64URLEncode(val) {
  var encodedVal = btoa(val);
  encodedVal = encodedVal.replace(/\+/g, '-') // Convert '+' to '-'
  .replace(/\//g, '_') // Convert '/' to '_'
  .replace(/=+$/, ''); // Remove ending '='

  return encodedVal;
};
//# sourceMappingURL=initialize.js.map